import { CONTRACT, classifyQuery, makeEventId, moneyFromShopifyCents, normalizeShopifyGid, safePagePath, safeReplayContext, sanitizeTargetUrl, validateEvent } from './core.mjs';

const CHANNEL = 'tyche:commerce_event_v1';
const PENDING_KEY = 'tyche.analytics.pending.v1';
const CONTACT_PENDING_KEY = 'tyche.analytics.contact-pending.v1';
const TRAFFIC_KEY = 'tyche.analytics.traffic.v1';
const PENDING_TTL_MS = 30_000;
const QUEUE_LIMIT = 50;
const THEME_MAX_INFLIGHT = 4;
const QUEUE_TTL_MS = 10_000;
const PUBLISH_RETRY_MS = [250, 1000, 2000];
const PHASE2_EVENTS = new Set([
  'product_detail_viewed', 'product_detail_opened', 'product_list_item_viewed', 'product_media_interacted',
  'cart_updated', 'contact_cta_clicked', 'ui_request_failed', 'form_submission_failed',
]);
const EXPERIMENT_EVENTS = new Set(['experiment_exposed']);

const privacyError = (value) => typeof value === 'string'
  && (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
    || /^[A-HJ-NPR-Z0-9]{17}$/i.test(value)
    || /^\+?[\d() .-]{7,20}$/.test(value)
    || /https?:\/\//i.test(value));

export function startStorefrontAnalytics({ win = window, doc = document, storage = null, now = () => Date.now(), schedule = setTimeout } = {}) {
  const contextElement = doc.getElementById('tyche-analytics-context');
  let config;
  try { config = JSON.parse(contextElement?.textContent ?? '{}'); } catch { config = {}; }
  if (!config.enabled || !config.shop_domain) {
    const disabled = Object.freeze({
      capture: () => null,
      startCartOperation: () => null,
      cartSucceeded: () => null,
      cartFailed: () => null,
      startCartBatchOperation: () => null,
      cartBatchSucceeded: () => [],
      cartBatchFailed: () => [],
      cartUpdated: () => null,
      cartActionFailed: () => null,
      cartUpdatesFailed: () => [],
      createOperationId: () => null,
      trackFilterChange: () => null,
      getCurrentFilters: () => ({}),
      trackCartView: () => null,
      uiFailure: () => null,
      prepareSearchNavigation: () => null,
      onResultsRendered: () => null,
      onRecommendationsRendered: () => null,
      setReplayContext: () => null,
      getDiagnostics: () => ({ state: 'disabled', queue: 0 }),
      revokeConsent: () => null,
    });
    win.TycheAnalytics = disabled;
    return disabled;
  }

  const tabStorage = storage ?? (() => {
    try { return win.sessionStorage; } catch { return null; }
  })();
  let state = 'UNKNOWN';
  let epoch = 0;
  let allowedSince = 0;
  let pageInstanceId = null;
  let trafficType = config.environment === 'production' ? 'external' : 'test';
  let initializedPage = false;
  let publishQueue = [];
  let publishInFlight = 0;
  let pendingSearch = null;
  let contextSearch = null;
  let cartOperations = new Map();
  let phase2Count = 0;
  let droppedCount = 0;
  const resultStates = new WeakMap();
  const observedCards = new WeakSet();
  const viewedDetails = new Set();
  const exposedExperiments = new WeakSet();
  const exposedExperimentKeys = new Set();
  const pageExposures = new Set();
  const pendingCardExposure = new Map();
  const pendingDetailExposure = new Map();
  let searchObserver = null;
  let policyObserver = null;
  let domObserver = null;
  let mainProductObserver = null;

  function currentPrivacyAllowed() {
    try {
      return win.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
    } catch {
      return false;
    }
  }

  function safeRead(key) {
    if (state !== 'ALLOWED' || !tabStorage) return null;
    try { return tabStorage.getItem(key); } catch { return null; }
  }

  function safeWrite(key, value) {
    if (state !== 'ALLOWED' || !tabStorage) return false;
    try { tabStorage.setItem(key, value); return true; } catch { return false; }
  }

  function safeRemove(key) {
    try { tabStorage?.removeItem(key); } catch { /* storage may be disabled */ }
  }

  function createId() {
    const id = makeEventId(win.crypto?.randomUUID?.bind(win.crypto));
    if (!id) droppedCount += 1;
    return id;
  }

  function pageType() {
    return config.page_type || 'other';
  }

  function pageContext() {
    return {
      page_type: pageType(),
      page_path: safePagePath(win.location?.pathname ?? '/'),
      page_instance_id: pageInstanceId,
    };
  }

  function safeFilterValues(input) {
    const output = {};
    if (!input || typeof input !== 'object' || Array.isArray(input)) return output;
    for (const key of ['part_type', 'vehicle_brand', 'vehicle_model', 'placement', 'condition', 'availability']) {
      const values = Array.isArray(input[key]) ? input[key] : input[key] == null ? [] : [input[key]];
      const clean = [...new Set(values.filter((value) => typeof value === 'string' && value.length <= 128 && !privacyError(value)).map((value) => value.normalize('NFKC').trim()).filter(Boolean))].slice(0, 20);
      if (clean.length) output[key] = clean;
    }
    for (const key of ['price_min', 'price_max']) {
      if (input[key] === null || input[key] === undefined) continue;
      const value = Number(input[key]);
      if (Number.isFinite(value) && value >= 0) output[key] = value;
    }
    return output;
  }

  function collectFilterValues(root = doc) {
    const found = {};
    const names = new Map([
      ['parts-type', 'part_type'], ['category', 'part_type'], ['desktop-parts-type', 'part_type'], ['mobile-parts-type', 'part_type'],
      ['brand', 'vehicle_brand'], ['desktop-brand', 'vehicle_brand'], ['mobile-brand', 'vehicle_brand'],
      ['model', 'vehicle_model'], ['desktop-model', 'vehicle_model'], ['mobile-model', 'vehicle_model'],
      ['placement', 'placement'], ['desktop-placement', 'placement'], ['mobile-placement', 'placement'], ['condition', 'condition'], ['mobile-condition', 'condition'],
    ]);
    for (const input of root.querySelectorAll?.('.mobile-filter-select, #pc-filter-sidebar input[type="checkbox"]:checked, #mobile-filter-slider input[type="checkbox"]:checked, facet-filters-form input[type="checkbox"]:checked') ?? []) {
      if (!input.value || !input.checked && input.type === 'checkbox') continue;
      let key = names.get(input.id);
      if (!key && input.type === 'checkbox') {
        const param = (input.getAttribute('data-param-name') || input.name || input.getAttribute('data-filter') || '').toLowerCase();
        if (param.includes('vendor') || param.includes('brand')) key = 'vehicle_brand';
        else if (param.includes('model')) key = 'vehicle_model';
        else if (param.includes('placement')) key = 'placement';
        else if (param.includes('condition')) key = 'condition';
        else if (param.includes('category') || param.includes('product_type') || param.includes('parts')) key = 'part_type';
      }
      if (key) (found[key] ??= []).push(input.value);
    }
    for (const input of root.querySelectorAll?.('facet-filters-form input[type="number"]') ?? []) {
      const value = Number(input.value);
      if (!input.value || !Number.isFinite(value) || value < 0) continue;
      const name = input.name.toLowerCase();
      if (name.includes('gte') || name.includes('min')) found.price_min = value;
      else if (name.includes('lte') || name.includes('max')) found.price_max = value;
    }
    return safeFilterValues(found);
  }

  function setConsent(next) {
    if (next !== 'ALLOWED') {
      if (state !== 'DENIED') epoch += 1;
      state = 'DENIED';
      allowedSince = 0;
      publishQueue = [];
      pendingSearch = null;
      contextSearch = null;
      cartOperations.clear();
      searchObserver?.disconnect();
      policyObserver?.disconnect();
      domObserver?.disconnect();
      mainProductObserver?.disconnect();
      searchObserver = null;
      policyObserver = null;
      domObserver = null;
      mainProductObserver = null;
      viewedDetails.clear();
      pageExposures.clear();
      exposedExperimentKeys.clear();
      for (const timer of [...pendingCardExposure.values(), ...pendingDetailExposure.values()]) clearTimeout(timer);
      pendingCardExposure.clear();
      pendingDetailExposure.clear();
      initializedPage = false;
      safeRemove(PENDING_KEY);
      safeRemove(CONTACT_PENDING_KEY);
      safeRemove(TRAFFIC_KEY);
      try { win.posthog?.opt_out_capturing?.(); } catch { /* analytics must never break the storefront */ }
      return;
    }
    if (!currentPrivacyAllowed()) return;
    if (state !== 'ALLOWED') {
      state = 'ALLOWED';
      epoch += 1;
      allowedSince = now();
      const marker = safeRead(TRAFFIC_KEY);
      if (marker === 'internal' || marker === 'test') trafficType = marker;
      pendingSearch = null;
      contextSearch = null;
      pageInstanceId = createId();
      initializedPage = false;
      initializePage();
      drainQueue();
    }
  }

  function consentChanged(event) {
    const allowed = [
      event?.detail?.analyticsAllowed,
      event?.detail?.analyticsProcessingAllowed,
      event?.customerPrivacy?.analyticsProcessingAllowed,
    ].find((value) => typeof value === 'boolean');
    if (typeof allowed !== 'boolean') return;
    setConsent(allowed ? 'ALLOWED' : 'DENIED');
  }

  function setupConsent() {
    doc.addEventListener('visitorConsentCollected', consentChanged);
    const onFeature = (error) => {
      if (error) return;
      setConsent(currentPrivacyAllowed() ? 'ALLOWED' : 'DENIED');
    };
    try {
      if (typeof win.Shopify?.loadFeatures === 'function') {
        win.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], onFeature);
      }
    } catch { /* remain unknown until Shopify reports the decision */ }
  }

  function allowedForEpoch(expectedEpoch) {
    return state === 'ALLOWED' && epoch === expectedEpoch && currentPrivacyAllowed();
  }

  function diagnostics(reason) {
    if (config.environment === 'production') return;
    try {
      win.dispatchEvent(new CustomEvent('tyche:analytics_diagnostic', { detail: { reason, dropped: droppedCount, queued: publishQueue.length } }));
    } catch { /* diagnostics are optional */ }
  }

  function drainQueue() {
    if (state !== 'ALLOWED') return;
    while (publishInFlight < THEME_MAX_INFLIGHT && publishQueue.length) {
      const record = publishQueue[0];
      if (record.epoch !== epoch || now() - record.created_at > QUEUE_TTL_MS) {
        publishQueue.shift();
        droppedCount += 1;
        continue;
      }
      if (record.retry_at > now()) {
        if (!record.retryScheduled) {
          record.retryScheduled = true;
          schedule(() => { record.retryScheduled = false; drainQueue(); }, record.retry_at - now());
        }
        return;
      }
      const publish = win.Shopify?.analytics?.publish;
      if (typeof publish !== 'function') {
        if (record.attempt >= PUBLISH_RETRY_MS.length) {
          publishQueue.shift();
          droppedCount += 1;
          continue;
        }
        if (!record.retryScheduled) {
          record.retryScheduled = true;
          record.retry_at = now() + PUBLISH_RETRY_MS[record.attempt++];
          schedule(() => { record.retryScheduled = false; drainQueue(); }, record.retry_at - now());
        }
        return;
      }
      publishQueue.shift();
      publishInFlight += 1;
      const retry = () => {
        publishInFlight -= 1;
        if (state !== 'ALLOWED' || record.epoch !== epoch || now() - record.created_at > QUEUE_TTL_MS || record.attempt >= PUBLISH_RETRY_MS.length) {
          droppedCount += 1;
          drainQueue();
          return;
        }
        if (publishQueue.length >= QUEUE_LIMIT) { publishQueue.shift(); droppedCount += 1; }
        record.retry_at = now() + PUBLISH_RETRY_MS[record.attempt++];
        publishQueue.push(record);
        drainQueue();
      };
      try {
        Promise.resolve(publish.call(win.Shopify.analytics, CHANNEL, record.properties))
          .then(() => { publishInFlight -= 1; drainQueue(); }, retry);
      } catch { retry(); }
    }
  }

  function getReplay() {
    let replayHost = config.replay_host;
    if (!replayHost && config.api_host) {
      try { replayHost = new URL(config.api_host).origin.replace('.i.posthog.com', '.posthog.com'); } catch { replayHost = null; }
    }
    const replay = safeReplayContext(win.posthog, replayHost || 'https://us.posthog.com', String(config.replay_project_id || '639039'));
    if (replay.replay_url) replay.replay_url = replay.replay_url.slice(0, 512);
    return replay;
  }

  function capture(eventName, values = {}) {
    if (state !== 'ALLOWED' || !currentPrivacyAllowed()) return null;
    if (PHASE2_EVENTS.has(eventName) && !config.phase2_enabled) return null;
    if (EXPERIMENT_EVENTS.has(eventName) && !config.experiments_enabled) return null;
    const eventId = values.event_id || createId();
    if (!eventId) return null;
    const eventContract = CONTRACT.events.find((candidate) => candidate.name === eventName);
    if (!eventContract) return null;
    const hasPageInstance = eventContract.required.includes('page_instance_id') || eventContract.optional.includes('page_instance_id');
    const replay = getReplay();
    const businessValues = { ...values };
    delete businessValues.replay_session_id;
    delete businessValues.replay_url;
    delete businessValues.replay_status;
    const properties = {
      ...businessValues,
      ...(replay.replay_status === 'available' ? replay : {}),
      event_id: eventId,
      occurred_at: values.occurred_at || new Date(now()).toISOString(),
      taxonomy_version: 1,
      event_domain: 'commerce',
      environment: config.environment || 'preview',
      traffic_type: trafficType,
      release_id: config.release_id || 'unreleased',
      ...(eventName === 'storefront_page_viewed' ? { page_type: pageType(), page_path: safePagePath(win.location?.pathname ?? '/') } : {}),
      ...(hasPageInstance ? { page_instance_id: pageInstanceId } : {}),
    };
    delete properties.distinct_id;
    delete properties.event_source;
    delete properties.consent_basis;
    delete properties.shop_domain;
    const validation = validateEvent(eventName, properties, { partial: true, source: 'theme_custom' });
    if (!validation.ok) { droppedCount += 1; diagnostics(`invalid:${eventName}`); return null; }
    const record = { properties: { event: eventName, ...properties }, epoch, created_at: now(), attempt: 0, retry_at: 0, retryScheduled: false };
    if (publishQueue.length >= QUEUE_LIMIT) { publishQueue.shift(); droppedCount += 1; }
    publishQueue.push(record);
    drainQueue();
    return eventId;
  }

  function initializePage() {
    if (initializedPage || state !== 'ALLOWED') return;
    initializedPage = true;
    const path = safePagePath(win.location?.pathname ?? '/');
    const query = new URLSearchParams(win.location?.search || '');
    const waitingForSearchNormalization = pageType() === 'search' && (!query.has('q') || !query.get('q'));
    if (!waitingForSearchNormalization) capture('storefront_page_viewed', {
      page_type: pageType(),
      page_path: path,
      ...(config.landing_source ? { landing_source: config.landing_source } : {}),
    });
    const replay = getReplay();
    win.dispatchEvent(new CustomEvent('tyche:analytics_replay_context', { detail: replay }));
    if (pageType() === 'cart') void recordCartView('page');
    if (pageType() === 'product') observePrimaryProduct();
    recordContactSuccess();
    observeResults();
    if (config.phase2_enabled) observeExposures();
  }

  function allowedSearchTarget(target) {
    if (typeof target !== 'string') return null;
    return sanitizeTargetUrl(target);
  }

  function prepareSearchNavigation(target, options = {}) {
    if (state !== 'ALLOWED') return null;
    const safeTarget = allowedSearchTarget(target);
    if (!safeTarget) return null;
    const query = classifyQuery(options.query_text ?? '', options.allowed_queries ?? []);
    const isExplicit = options.search_method === 'text' || options.search_method === 'vehicle_filters' || options.search_method === 'mixed';
    const searchId = isExplicit ? createId() : null;
    const eventId = isExplicit ? capture('search_submitted', {
      search_id: searchId,
      search_method: options.search_method,
      query_type: options.query_text === '*' ? 'empty' : query.query_type,
      ...(query.query_normalized ? { query_normalized: query.query_normalized } : {}),
      ...(query.query_redacted ? { query_redacted: true } : {}),
      entry_point: options.entry_point || 'other',
      ...(Object.keys(options.active_filters ?? {}).length ? { active_filters: safeFilterValues(options.active_filters) } : {}),
    }) : null;
    const currentFilters = safeFilterValues(options.active_filters ?? collectFilterValues());
    const hasCurrentFilters = Object.values(currentFilters).some((values) => Array.isArray(values) ? values.length > 0 : values !== null && values !== undefined);
    const filterApplyId = options.apply_method && (options.apply_method !== 'reset' || hasCurrentFilters) ? createId() : null;
    const carriedSearch = !eventId && contextSearch?.search_id && !contextSearch.query_redacted
      ? {
          search_id: contextSearch.search_id,
          search_method: contextSearch.search_method,
          query_type: contextSearch.query_type,
          ...(contextSearch.query_normalized ? { query_normalized: contextSearch.query_normalized } : {}),
        }
      : {};
    const pending = {
      target: safeTarget,
      created_at: now(),
      query_redacted: query.query_redacted === true,
      ...(eventId ? { search_id: searchId, search_event_id: eventId, search_method: options.search_method, query_type: options.query_text === '*' ? 'empty' : query.query_type, ...(query.query_normalized ? { query_normalized: query.query_normalized } : {}), ...(query.query_redacted ? { query_redacted: true } : {}) } : {}),
      ...carriedSearch,
      ...(filterApplyId ? { filter_apply_id: filterApplyId, apply_method: options.apply_method, active_filters: options.apply_method === 'reset' ? {} : currentFilters } : {}),
      result_origin: options.apply_method === 'reset' && !filterApplyId ? 'direct' : options.result_origin || (filterApplyId ? 'filter_change' : 'user_search'),
    };
    pendingSearch = pending;
    contextSearch = pending.search_id ? pending : contextSearch;
    safeWrite(PENDING_KEY, JSON.stringify(pending));
    return eventId || filterApplyId;
  }

  function loadPending() {
    if (pendingSearch) return pendingSearch;
    const encoded = safeRead(PENDING_KEY);
    if (!encoded) return null;
    try {
      const value = JSON.parse(encoded);
      if (now() - value.created_at > PENDING_TTL_MS) { safeRemove(PENDING_KEY); return null; }
      const current = allowedSearchTarget(win.location?.href);
      if (!current || current !== value.target) return null;
      if (value.query_redacted) {
        delete value.search_id;
        delete value.search_event_id;
        delete value.search_method;
        delete value.query_type;
        delete value.query_normalized;
        delete value.filter_apply_id;
        delete value.apply_method;
        delete value.active_filters;
        value.result_origin = 'direct';
      }
      pendingSearch = value;
      contextSearch = value.search_id ? value : null;
      safeRemove(PENDING_KEY);
      return value;
    } catch {
      safeRemove(PENDING_KEY);
      return null;
    }
  }

  function readResults(container) {
    if (!container || !container.isConnected) return null;
    const cards = [...container.querySelectorAll('[data-analytics-card="true"]')];
    const count = Number(container.dataset.resultsCount);
    const parsedCount = Number.isSafeInteger(count) && count >= 0 ? count : null;
    return { cards, results_count: parsedCount, signature: `${parsedCount ?? 'x'}:${cards.map((card) => card.dataset.analyticsProductId).join(',')}` };
  }

  function visibleResults() {
    const containers = [...doc.querySelectorAll('[data-analytics-results]')].filter((node) => node.getClientRects().length && !node.closest('[hidden], [aria-hidden="true"]'));
    return containers.sort((a, b) => b.getBoundingClientRect().width * b.getBoundingClientRect().height - a.getBoundingClientRect().width * a.getBoundingClientRect().height)[0] ?? null;
  }

  function onResultsRendered(container = visibleResults()) {
    if (state !== 'ALLOWED' || !container || !/\/search\/?$/i.test(win.location?.pathname ?? '')) return null;
    const params = new URLSearchParams(win.location.search);
    if (!params.has('q') || !params.get('q')) return null;
    const result = readResults(container);
    if (!result) return null;
    const previous = resultStates.get(container);
    if (previous?.signature === result.signature) return previous.result_set_id;
    const resultSetId = createId();
    const pending = loadPending();
    const page = Math.max(1, Number(params.get('page')) || 1);
    const collectionRedirect = !pending && Boolean(doc.referrer) && (() => {
      try { return new URL(doc.referrer).pathname.startsWith('/collections/'); } catch { return false; }
    })();
    const origin = pending?.result_origin || (page > 1 ? 'pagination' : collectionRedirect ? 'collection_redirect' : 'direct');
    const properties = {
      result_set_id: resultSetId,
      results_count: result.results_count ?? result.cards.length,
      rendered_product_count: result.cards.length,
      results_scope: container.dataset.resultsScope || 'unknown',
      result_origin: origin,
      page_number: page,
      sort_key: params.get('sort_by')?.slice(0, 48) || 'relevance',
      ...(pending?.search_id ? { search_id: pending.search_id } : {}),
      ...(pending?.query_type ? { query_type: pending.query_type } : {}),
      ...(pending?.query_normalized ? { query_normalized: pending.query_normalized } : {}),
      ...(pending?.query_redacted ? { query_redacted: true } : {}),
      ...(pending?.active_filters ? { active_filters: pending.active_filters } : collectFilterValues()),
      ...(pending?.created_at && now() >= pending.created_at ? { load_ms: Math.min(3_600_000, now() - pending.created_at) } : {}),
    };
    capture('search_results_viewed', properties);
    if (pending?.filter_apply_id) {
      const surface = /mobile/i.test(pending.apply_method || '') || win.innerWidth < 769 ? 'mobile_sidebar' : 'desktop_sidebar';
      capture('filter_applied', {
        filter_apply_id: pending.filter_apply_id,
        result_set_id: resultSetId,
        active_filters: properties.active_filters || {},
        results_count: properties.results_count,
        rendered_product_count: properties.rendered_product_count,
        results_scope: properties.results_scope,
        apply_method: pending.apply_method,
        surface,
      });
    }
    const stateEntry = { signature: result.signature, result_set_id: resultSetId };
    resultStates.set(container, stateEntry);
    if (searchObserver) for (const card of result.cards) searchObserver.observe(card);
    return resultSetId;
  }

  function onRecommendationsRendered(container) {
    if (state !== 'ALLOWED' || !config.phase2_enabled || !searchObserver || !container) return;
    if (container.matches?.('[data-analytics-card="true"]')) searchObserver.observe(container);
    for (const card of container.querySelectorAll?.('[data-analytics-card="true"]') ?? []) searchObserver.observe(card);
  }

  function observeResults() {
    if (!win.IntersectionObserver) return;
    searchObserver = new IntersectionObserver((entries) => {
      if (state !== 'ALLOWED' || !config.phase2_enabled) return;
      for (const entry of entries) {
        const card = entry.target;
        if (!entry.isIntersecting || entry.intersectionRatio < 0.5) {
          const timer = pendingCardExposure.get(card);
          if (timer) clearTimeout(timer);
          pendingCardExposure.delete(card);
          continue;
        }
        if (observedCards.has(card)) continue;
        if (pendingCardExposure.has(card)) continue;
        const id = card.dataset.analyticsProductId;
        const listType = card.dataset.analyticsListType || (card.closest('[data-analytics-results]') ? 'search' : 'featured');
        const currentContainer = card.closest('[data-analytics-results]');
        const listId = currentContainer ? resultStates.get(currentContainer)?.result_set_id : pageInstanceId;
        const placement = card.dataset.analyticsPlacement || (currentContainer ? 'search_grid' : 'home_grid');
        if (!id || !listId) continue;
        const key = `${id}:${card.dataset.analyticsRecommendationId || listId}:${placement}`;
        if (pageExposures.has(key) || pageExposures.size >= 24) continue;
        pendingCardExposure.set(card, schedule(() => {
          pendingCardExposure.delete(card);
          if (state !== 'ALLOWED' || doc.hidden || !card.isConnected || !card.getClientRects().length || observedCards.has(card)) return;
          observedCards.add(card);
          pageExposures.add(key);
          const detail = { product_id: id, list_type: listType, position: Number(card.dataset.analyticsPosition) || 1, placement };
          if (listType === 'search') detail.result_set_id = listId;
          if (listType === 'recommendation') detail.recommendation_id = card.dataset.analyticsRecommendationId;
          if (contextSearch?.search_id) detail.search_id = contextSearch.search_id;
          capture('product_list_item_viewed', { ...detail, exposure_id: createId() });
        }, 1000));
      }
    }, { threshold: [0.5], rootMargin: '0px' });
    policyObserver = new IntersectionObserver((entries) => {
      if (state !== 'ALLOWED' || !config.phase2_enabled) return;
      for (const entry of entries) {
        const heading = entry.target;
        if (!entry.isIntersecting || entry.intersectionRatio < 0.5) {
          const timer = pendingDetailExposure.get(heading);
          if (timer) clearTimeout(timer);
          pendingDetailExposure.delete(heading);
          continue;
        }
        const detailType = heading.dataset.analyticsDetailType;
        const id = heading.dataset.analyticsExposureId || `${config.product_id || 'unknown'}:${detailType}`;
        if (!detailType || viewedDetails.has(id) || pendingDetailExposure.has(heading)) continue;
        pendingDetailExposure.set(heading, schedule(() => {
          pendingDetailExposure.delete(heading);
          if (state !== 'ALLOWED' || doc.hidden || !heading.isConnected || !heading.getClientRects().length || viewedDetails.has(id)) return;
          viewedDetails.add(id);
          capture('product_detail_viewed', { product_id: config.product_id, detail_type: detailType, exposure_id: createId() });
        }, 1000));
      }
    }, { threshold: [0.5], rootMargin: '0px' });
    for (const heading of doc.querySelectorAll('[data-analytics-detail-type]')) policyObserver.observe(heading);
    domObserver = new MutationObserver((records) => {
      for (const record of records) {
        const element = record.target?.nodeType === 1 ? record.target : record.target?.parentElement;
        const results = element?.closest?.('[data-analytics-results]');
        if (results) onResultsRendered(results);
        const recommendations = element?.closest?.('product-recommendations');
        if (recommendations) onRecommendationsRendered(recommendations);
        for (const node of record.addedNodes ?? []) {
          if (node.nodeType !== 1) continue;
          if (node.matches?.('[data-analytics-card="true"]')) searchObserver?.observe(node);
          for (const card of node.querySelectorAll?.('[data-analytics-card="true"]') ?? []) searchObserver?.observe(card);
          if (node.matches?.('[data-analytics-detail-type]')) policyObserver?.observe(node);
          for (const heading of node.querySelectorAll?.('[data-analytics-detail-type]') ?? []) policyObserver?.observe(heading);
        }
      }
    });
    for (const container of doc.querySelectorAll('[data-analytics-results], product-recommendations')) domObserver.observe(container, { childList: true, subtree: true });
    for (const control of doc.querySelectorAll('.mobile-filter-select, #pc-filter-sidebar input[type="checkbox"], #mobile-filter-slider input[type="checkbox"], facet-filters-form input[type="checkbox"], facet-filters-form input[type="number"], facet-filters-form select')) {
      control.dataset.analyticsPrevious = JSON.stringify(controlValues(control));
    }
    onResultsRendered();
  }

  function observeExposures() {
    if (state !== 'ALLOWED' || !config.phase2_enabled || !win.IntersectionObserver) return;
    if (!searchObserver) {
      searchObserver = new IntersectionObserver((entries) => {
        if (state !== 'ALLOWED' || !config.phase2_enabled) return;
        for (const entry of entries) {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.5) continue;
          const card = entry.target;
          if (observedCards.has(card)) continue;
          const id = card.dataset.analyticsProductId;
          const listType = card.dataset.analyticsListType || 'featured';
          const currentContainer = card.closest('[data-analytics-results]');
          const listId = currentContainer ? resultStates.get(currentContainer)?.result_set_id : pageInstanceId;
          const placement = card.dataset.analyticsPlacement || 'home_grid';
          if (!id || !listId) continue;
          const key = `${id}:${card.dataset.analyticsRecommendationId || listId}:${placement}`;
          if (pageExposures.has(key) || pageExposures.size >= 24) continue;
          if (listType === 'recommendation' && !card.dataset.analyticsRecommendationId) continue;
          observedCards.add(card);
          pageExposures.add(key);
          const detail = { product_id: id, list_type: listType, position: Number(card.dataset.analyticsPosition) || 1, placement };
          if (listType === 'search' && listId) detail.result_set_id = listId;
          if (listType === 'recommendation') detail.recommendation_id = card.dataset.analyticsRecommendationId;
          if (contextSearch?.search_id) detail.search_id = contextSearch.search_id;
          capture('product_list_item_viewed', { ...detail, exposure_id: createId() });
        }
      }, { threshold: [0.5] });
    }
    if (!policyObserver) {
      policyObserver = new IntersectionObserver((entries) => {
        if (state !== 'ALLOWED' || !config.phase2_enabled) return;
        for (const entry of entries) {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.5) continue;
          const heading = entry.target;
          const detailType = heading.dataset.analyticsDetailType;
          const exposureKey = heading.dataset.analyticsExposureId || `${config.product_id || 'unknown'}:${detailType}`;
          if (!detailType || viewedDetails.has(exposureKey)) continue;
          viewedDetails.add(exposureKey);
          capture('product_detail_viewed', { product_id: config.product_id, detail_type: detailType, exposure_id: createId() });
        }
      }, { threshold: [0.5] });
    }
    for (const card of doc.querySelectorAll('[data-analytics-card="true"]')) searchObserver.observe(card);
    for (const heading of doc.querySelectorAll('[data-analytics-detail-type]')) policyObserver.observe(heading);
    observeExperimentTargets();
  }

  function observeExperimentTargets() {
    if (!config.experiments_enabled || !Array.isArray(config.experiment_keys) || !win.IntersectionObserver) return;
    const allowlist = new Set(config.experiment_keys.map((key) => String(key).trim()).filter(Boolean));
    if (!allowlist.size) return;
    const observer = new IntersectionObserver((entries) => {
      if (state !== 'ALLOWED' || doc.hidden || !config.experiments_enabled) return;
      for (const entry of entries) {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.5 || exposedExperiments.has(entry.target)) continue;
        const key = entry.target.dataset.analyticsExperimentKey;
        const variant = entry.target.dataset.analyticsVariantKey;
        if (!key || !allowlist.has(key) || !variant || exposedExperimentKeys.size >= 24) continue;
        exposedExperiments.add(entry.target);
        const identity = `${key}:${variant}`;
        if (exposedExperimentKeys.has(identity)) continue;
        exposedExperimentKeys.add(identity);
        capture('experiment_exposed', { experiment_key: key, variant_key: variant, exposure_id: createId() });
      }
    }, { threshold: [0.5] });
    for (const node of doc.querySelectorAll('[data-analytics-experiment-key][data-analytics-variant-key]')) observer.observe(node);
  }

  function captureSelection(anchor, event) {
    if (state !== 'ALLOWED' || !anchor?.dataset.analyticsProductId) return;
    const container = anchor.closest('[data-analytics-results]');
    const listType = anchor.dataset.analyticsListType || (container ? 'search' : 'featured');
    const position = Number(anchor.dataset.analyticsPosition);
    if (!Number.isSafeInteger(position) || position < 1) return;
    const navigationMode = anchor.target === '_blank' || event?.metaKey || event?.ctrlKey ? 'new_context' : 'same_tab';
    const properties = {
      product_id: anchor.dataset.analyticsProductId,
      list_type: listType,
      position,
      placement: anchor.dataset.analyticsPlacement || (container ? 'search_grid' : 'home_grid'),
      page_instance_id: pageInstanceId,
      navigation_mode: navigationMode,
    };
    const resultSetId = container ? resultStates.get(container)?.result_set_id : null;
    if (listType === 'search') {
      if (!resultSetId) return;
      properties.result_set_id = resultSetId;
    }
    if (listType === 'recommendation') {
      if (!anchor.dataset.analyticsRecommendationId) return;
      properties.recommendation_id = anchor.dataset.analyticsRecommendationId;
    }
    if (contextSearch?.search_id) properties.search_id = contextSearch.search_id;
    capture('product_selected', properties);
  }

  function filterName(control) {
    const value = `${control.id || ''} ${control.dataset.filter || ''} ${control.dataset.paramName || ''}`.toLowerCase();
    if (/brand|vendor/.test(value)) return 'vehicle_brand';
    if (/model/.test(value)) return 'vehicle_model';
    if (/placement/.test(value)) return 'placement';
    if (/condition/.test(value)) return 'condition';
    if (/availability/.test(value)) return 'availability';
    if (/price/.test(value)) return 'price';
    if (/category|parts|product.?type/.test(value)) return 'part_type';
    return 'other';
  }

  function filterSurface(control) {
    if (control.closest('#FacetFiltersFormMobile, .mobile-facets')) return 'mobile_sidebar';
    if (control.closest('#FacetFiltersForm, .facets-container, #FacetsWrapperDesktop')) return 'desktop_sidebar';
    if (control.closest('#mobile-filter-slider, .mobile-search-filters, #mobile-filter-sidebar')) return 'mobile_sidebar';
    if (control.closest('#pc-filter-sidebar')) return 'desktop_sidebar';
    return win.innerWidth < 769 ? 'mobile_home' : 'desktop_home';
  }

  function controlValues(control) {
    if (control.type === 'checkbox') return control.checked ? [control.value] : [];
    return control.value ? [control.value] : [];
  }

  function trackFilterChange({ filter_name, previous_values = [], new_values = [], change_reason = 'user', surface = 'search_bar' } = {}) {
    if (state !== 'ALLOWED' || !['part_type', 'vehicle_brand', 'vehicle_model', 'placement', 'condition', 'availability', 'price', 'other'].includes(filter_name)) return null;
    const clean = (values) => [...new Set((Array.isArray(values) ? values : [values]).filter((value) => typeof value === 'string' && value.length <= 128 && !privacyError(value)).map((value) => value.normalize('NFKC').trim()).filter(Boolean))].slice(0, 20);
    const previous = clean(previous_values);
    const next = clean(new_values);
    if (JSON.stringify(previous) === JSON.stringify(next)) return null;
    return capture('filter_changed', {
      filter_change_id: createId(), filter_name, previous_values: previous, new_values: next,
      change_reason: change_reason === 'dependency_reset' ? 'dependency_reset' : 'user',
      surface: ['desktop_home', 'mobile_home', 'desktop_sidebar', 'mobile_sidebar', 'search_bar'].includes(surface) ? surface : 'search_bar',
      ...(contextSearch?.search_id ? { search_id: contextSearch.search_id } : {}),
    });
  }

  function beforeFilterChange(control) {
    if (state !== 'ALLOWED' || !config.enabled || !control?.matches('input[type="checkbox"],input[type="number"],select')) return;
    if (control.name === 'sort_by' || /sort/i.test(control.id || '')) return;
    const name = filterName(control);
    const reason = control.dataset.analyticsProgrammatic === 'true' ? 'dependency_reset' : 'user';
    let previous = [];
    try { previous = control.dataset.analyticsPrevious ? JSON.parse(control.dataset.analyticsPrevious) : controlValues(control); } catch { previous = []; }
    const next = controlValues(control);
    trackFilterChange({ filter_name: name, previous_values: previous, new_values: next, change_reason: reason, surface: filterSurface(control) });
    control.dataset.analyticsPrevious = JSON.stringify(next);
  }

  function uiFailure(operationName, operationId, status, stage = 'transport') {
    if (!config.phase2_enabled) return;
    const code = status === 429 ? 'api_unavailable' : Number.isInteger(status) && status >= 400 ? 'http_error' : 'network_error';
    capture('ui_request_failed', { operation_id: operationId || createId(), operation_name: operationName, failure_stage: stage, error_code: code, ...(Number.isInteger(status) ? { http_status: status } : {}) });
  }

  function recordClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    const paginationLink = target?.closest('a.pagination__item, .pagination a[href]');
    if (paginationLink && /\/search\/?$/i.test(win.location?.pathname ?? '')) {
      prepareSearchNavigation(paginationLink.href, { result_origin: 'pagination' });
    }
    const productLink = target?.closest('a[data-analytics-product-id]');
    if (productLink) captureSelection(productLink, event);
    const media = target?.closest('[data-analytics-media-id]');
    if (media && config.phase2_enabled && config.product_id) {
      const action = media.dataset.analyticsMediaAction || (media.matches('[data-media-zoom]') ? 'zoom_open' : 'select');
      capture('product_media_interacted', { product_id: config.product_id, media_id: media.dataset.analyticsMediaId, media_action: action });
    }
    const cta = target?.closest('[data-analytics-contact-channel]');
    if (cta && config.phase2_enabled) capture('contact_cta_clicked', { contact_channel: cta.dataset.analyticsContactChannel, placement: cta.dataset.analyticsPlacement || 'other', ...(config.product_id ? { product_id: config.product_id } : {}) });
    const contactLink = target?.closest('a[href]');
    if (contactLink && config.phase2_enabled && !cta) {
      const href = contactLink.getAttribute('href') || '';
      const channel = /^mailto:/i.test(href) ? 'email' : /^tel:/i.test(href) ? 'phone' : /^\/pages\/contact(?:-us)?(?:\/|$)/i.test(href) ? 'contact_page' : null;
      if (channel) capture('contact_cta_clicked', { contact_channel: channel, placement: contactLink.dataset.analyticsPlacement || 'other', ...(config.product_id ? { product_id: config.product_id } : {}) });
    }
  }

  async function recordCartView(surface = 'page') {
    if (state !== 'ALLOWED' || pageType() === 'cart' && initializedPage === false) return null;
    try {
      const cartUrl = `${win.Shopify?.routes?.root || '/'}cart.js`;
      const response = await fetch(cartUrl, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!response.ok) { uiFailure('cart_refresh', createId(), response.status, 'refresh'); return null; }
      const cart = await response.json();
      if (!Number.isSafeInteger(cart.item_count) || cart.item_count < 0) { uiFailure('cart_refresh', createId(), undefined, 'response_parse'); return null; }
      return capture('cart_viewed', { cart_view_id: createId(), cart_surface: surface, item_count: cart.item_count });
    } catch {
      uiFailure('cart_refresh', createId(), undefined, 'transport');
      return null;
    }
  }

  function setupListeners() {
    doc.addEventListener('click', recordClick, true);
    doc.addEventListener('change', (event) => {
      const control = event.target;
      if (!(control instanceof Element)) return;
      beforeFilterChange(control);
      if (control.matches('#desktop-sort-select, #mobile-sort-select, .desktop-sort-select, .mobile-sort-select')) {
        const target = new URL(win.location.href);
        target.searchParams.set('sort_by', control.value);
        prepareSearchNavigation(target.toString(), { result_origin: 'sort' });
      }
    }, true);
    doc.addEventListener('submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.id === 'ContactForm') {
        const submissionId = createId();
        if (submissionId) safeWrite(CONTACT_PENDING_KEY, JSON.stringify({ submission_id: submissionId, form_id: 'ContactForm', created_at: now(), epoch }));
        return;
      }
      if (!/search/i.test(form.action)) return;
      const text = String(new FormData(form).get('q') ?? '');
      prepareSearchNavigation(form.action ? new URL(form.action, win.location.href).toString() : win.location.href, {
        search_method: 'text', query_text: text, entry_point: 'header',
      });
    }, true);
    doc.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const summary = target?.closest('details > summary');
      const details = summary?.parentElement;
      if (!details || details.open || !event.isTrusted || !config.phase2_enabled || !config.product_id) return;
      schedule(() => {
        if (state !== 'ALLOWED' || !details.open || !details.isConnected) return;
        capture('product_detail_opened', { product_id: config.product_id, detail_type: details.dataset.analyticsDetailType || 'other' });
      }, 0);
    }, true);
    doc.addEventListener('visibilitychange', () => {
      if (doc.hidden) {
        for (const timer of [...pendingCardExposure.values(), ...pendingDetailExposure.values()]) clearTimeout(timer);
        pendingCardExposure.clear();
        pendingDetailExposure.clear();
      } else {
        onResultsRendered();
        for (const card of doc.querySelectorAll('[data-analytics-card="true"]')) { searchObserver?.unobserve(card); searchObserver?.observe(card); }
        for (const heading of doc.querySelectorAll('[data-analytics-detail-type]')) { policyObserver?.unobserve(heading); policyObserver?.observe(heading); }
      }
    });
    const pending = loadPending();
    if (pending?.search_id) contextSearch = pending;
    const emailForm = doc.getElementById('signup_form');
      if (emailForm && emailForm.dataset.analyticsBound !== 'true') {
      emailForm.dataset.analyticsBound = 'true';
      emailForm.addEventListener('submit', () => {
          const submissionId = createId();
          if (submissionId) emailForm.dataset.analyticsSubmissionId = submissionId;
        }, true);
      }
  }

  function observePrimaryProduct() {
    const primary = doc.querySelector('product-info[id^="MainProduct-"]');
    if (!primary || !config.product_id || !config.price_context) return;
    let sent = false;
    const send = () => {
      if (sent || state !== 'ALLOWED' || !primary.getClientRects().length) return;
      sent = true;
      capture('product_viewed', {
        product_id: config.product_id,
        ...(config.variant_id ? { variant_id: config.variant_id } : {}),
        ...(config.product_vendor ? { product_vendor: config.product_vendor } : {}),
        ...(config.part_type ? { part_type: config.part_type } : {}),
        ...(config.condition ? { condition: config.condition } : {}),
        availability: config.availability || 'unknown',
        price_context: config.price_context,
      });
    };
    if (win.IntersectionObserver) {
      mainProductObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting && entry.intersectionRatio > 0)) {
          send();
          mainProductObserver?.disconnect();
        }
      }, { threshold: [0.01] });
      mainProductObserver.observe(primary);
    } else send();
  }

  function recordContactSuccess() {
    if (state !== 'ALLOWED') return;
    const success = Boolean(doc.querySelector('[data-analytics-contact-success="true"]'));
    const failed = Boolean(doc.querySelector('[data-analytics-contact-error="true"]'));
    if (!success && !failed) return;
    const saved = safeRead(CONTACT_PENDING_KEY);
    safeRemove(CONTACT_PENDING_KEY);
    if (!saved) return;
    try {
      const pending = JSON.parse(saved);
      if (pending.epoch !== epoch || now() - pending.created_at > PENDING_TTL_MS) return;
      if (success) capture('inquiry_submitted', { form_id: pending.form_id || 'ContactForm', submission_id: pending.submission_id });
      else if (config.phase2_enabled) capture('form_submission_failed', { form_id: pending.form_id || 'ContactForm', submission_id: pending.submission_id, error_code: 'validation' });
    } catch { droppedCount += 1; }
  }

  function trackCartView(surface) { return recordCartView(surface); }

  function startCartOperation(context = {}) {
    if (state !== 'ALLOWED' || !currentPrivacyAllowed()) return null;
    const operationId = createId();
    if (!operationId) return null;
    const variantId = normalizeShopifyGid(String(context.variant_id ?? ''), 'ProductVariant');
    const rawProductId = context.product_id || config.product_id;
    const productId = rawProductId ? normalizeShopifyGid(String(rawProductId), 'Product') : null;
    const quantity = Number(context.quantity);
    if (!variantId || !Number.isSafeInteger(quantity) || quantity < 1) return null;
    const safeContext = {
      operation_id: operationId,
      variant_id: variantId,
      quantity,
      entry_point: context.entry_point || 'other',
      ...(productId ? { product_id: productId } : {}),
      ...(context.price_context ? { price_context: context.price_context } : undefined),
    };
    const handle = { operationId, epoch };
    cartOperations.set(operationId, { handle, context: safeContext });
    capture('cart_add_requested', safeContext);
    return handle;
  }

  function startCartBatchOperation(contexts = []) {
    if (state !== 'ALLOWED' || !currentPrivacyAllowed() || !Array.isArray(contexts)) return null;
    const items = contexts.filter((context) => context && context.variant_id && Number.isSafeInteger(Number(context.quantity)) && Number(context.quantity) > 0).slice(0, 50);
    if (!items.length) return null;
    const operationId = createId();
    if (!operationId) return null;
    const normalized = items.map((context) => ({
      variant_id: normalizeShopifyGid(String(context.variant_id), 'ProductVariant'),
      quantity: Number(context.quantity),
      added_quantity: Number(context.quantity),
      previous_quantity: Number.isSafeInteger(Number(context.previous_quantity)) && Number(context.previous_quantity) >= 0 ? Number(context.previous_quantity) : 0,
      ...(context.product_id ? { product_id: normalizeShopifyGid(String(context.product_id), 'Product') } : {}),
      entry_point: context.entry_point || 'bulk_quick_add',
      price_context: context.price_context || { price_source: 'unknown', catalog_price: null, displayed_price: null, currency: null, pricing_mismatch: false },
    }));
    cartOperations.set(operationId, { handle: { operationId, epoch }, contexts: normalized });
    for (const item of normalized) capture('cart_add_requested', { operation_id: operationId, variant_id: item.variant_id.startsWith('gid://') ? item.variant_id : `gid://shopify/ProductVariant/${item.variant_id}`, quantity: item.quantity, entry_point: item.entry_point, ...(item.product_id ? { product_id: item.product_id } : {}) });
    return { operationId, epoch };
  }

  function cartBatchSucceeded(handle, cartItems, currency) {
    if (!handle || !Array.isArray(cartItems) || !allowedForEpoch(handle.epoch)) return [];
    const operation = cartOperations.get(handle.operationId);
    if (!operation?.contexts) return [];
    cartOperations.delete(handle.operationId);
    const events = [];
    for (const item of operation.contexts) {
      const current = cartItems.filter((line) => String(line.variant_id) === item.variant_id || `gid://shopify/ProductVariant/${line.variant_id}` === item.variant_id).reduce((total, line) => total + (Number(line.quantity) || 0), 0);
      const added = Math.max(0, current - item.previous_quantity);
      if (added > 0) {
        const properties = {
          operation_id: handle.operationId,
          variant_id: item.variant_id.startsWith('gid://') ? item.variant_id : `gid://shopify/ProductVariant/${item.variant_id}`,
          quantity: item.quantity,
          added_quantity: added,
          price_context: item.price_context,
          entry_point: item.entry_point,
          ...(item.product_id ? { product_id: item.product_id } : {}),
        };
        const line = cartItems.find((value) => String(value.variant_id) === item.variant_id || `gid://shopify/ProductVariant/${value.variant_id}` === item.variant_id);
        const originalUnitPrice = Number(line?.price);
        const finalUnitPrice = Number(line?.final_price ?? line?.price);
        if (Number.isFinite(originalUnitPrice) && originalUnitPrice >= 0 && Number.isFinite(finalUnitPrice) && finalUnitPrice >= 0 && /^[A-Z]{3}$/.test(currency || '')) {
          properties.price_context = {
            price_source: 'ajax_cart', catalog_price: originalUnitPrice / 100, displayed_price: finalUnitPrice / 100,
            currency, display_currency: currency, display_discount_percent: null, pricing_mismatch: false,
          };
          properties.value = finalUnitPrice * added / 100;
          properties.currency = currency;
        }
        events.push(capture('product_added_to_cart', properties));
      } else {
        events.push(capture('cart_action_failed', { operation_id: handle.operationId, action: 'add', failure_stage: 'mutation', error_code: 'quantity_limit', variant_id: item.variant_id.startsWith('gid://') ? item.variant_id : `gid://shopify/ProductVariant/${item.variant_id}` }));
      }
    }
    return events;
  }

  function cartBatchFailed(handle, error = {}) {
    if (!handle || !allowedForEpoch(handle.epoch)) return [];
    const operation = cartOperations.get(handle.operationId);
    if (!operation?.contexts) return [];
    cartOperations.delete(handle.operationId);
    const status = Number.isInteger(error.http_status) ? error.http_status : undefined;
    const code = ['out_of_stock', 'quantity_limit', 'validation', 'http_error', 'network_error', 'response_parse_error', 'api_unavailable', 'unknown'].includes(error.error_code) ? error.error_code : status === 422 ? 'out_of_stock' : status ? 'http_error' : 'network_error';
    return operation.contexts.map((item) => capture('cart_action_failed', { operation_id: handle.operationId, action: 'add', failure_stage: error.failure_stage || 'mutation', error_code: code, ...(status ? { http_status: status } : {}), variant_id: item.variant_id.startsWith('gid://') ? item.variant_id : `gid://shopify/ProductVariant/${item.variant_id}` }));
  }

  function cartUpdated({ operation_id, action, variant_id, previous_quantity, quantity, item_count, total_price, currency } = {}) {
    if (state !== 'ALLOWED' || !['change', 'remove', 'clear'].includes(action) || !Number.isSafeInteger(item_count) || item_count < 0) return null;
    const operationId = operation_id || createId();
    const properties = {
      operation_id: operationId,
      action,
      item_count,
      ...(variant_id ? { variant_id: normalizeShopifyGid(String(variant_id), 'ProductVariant') } : {}),
      ...(Number.isSafeInteger(previous_quantity) && previous_quantity >= 0 ? { previous_quantity } : {}),
      ...(Number.isSafeInteger(quantity) && quantity >= 0 ? { quantity } : {}),
    };
    const money = moneyFromShopifyCents(total_price, currency);
    if (money) { properties.value = money.value; properties.currency = money.currency; }
    return capture('cart_updated', properties);
  }

  function cartActionFailed({ operation_id, action, variant_id, error_code, http_status, failure_stage } = {}) {
    if (state !== 'ALLOWED' || !['add', 'change', 'remove', 'clear'].includes(action)) return null;
    const allowedCodes = ['out_of_stock', 'quantity_limit', 'validation', 'http_error', 'network_error', 'response_parse_error', 'api_unavailable', 'unknown'];
    const allowedStages = ['mutation', 'transport', 'response_parse', 'render', 'refresh'];
    return capture('cart_action_failed', {
      operation_id: operation_id || createId(),
      action,
      failure_stage: allowedStages.includes(failure_stage) ? failure_stage : 'mutation',
      error_code: allowedCodes.includes(error_code) ? error_code : 'unknown',
      ...(Number.isInteger(http_status) && http_status >= 0 && http_status <= 599 ? { http_status } : {}),
      ...(variant_id ? { variant_id: normalizeShopifyGid(String(variant_id), 'ProductVariant') } : {}),
    });
  }

  function cartUpdatesFailed({ operation_id, updates = [], error_code, http_status, failure_stage } = {}) {
    if (state !== 'ALLOWED' || !Array.isArray(updates)) return [];
    return updates.slice(0, 50).flatMap((update) => {
      if (!update || !['change', 'remove'].includes(update.action) || !update.variant_id) return [];
      const event = cartActionFailed({ ...update, operation_id, error_code, http_status, failure_stage });
      return event ? [event] : [];
    });
  }

  function cartSucceeded(handle, response) {
    if (!handle || !response || response.ok !== true || !allowedForEpoch(handle.epoch)) return null;
    const operation = cartOperations.get(handle.operationId);
    if (!operation) return null;
    cartOperations.delete(handle.operationId);
    const line = response.line_item || response;
    const returnedVariant = line.variant_id == null ? null : String(line.variant_id);
    const expectedVariant = String(operation.context.variant_id);
    const expectedVariantId = expectedVariant.startsWith('gid://') ? expectedVariant.split('/').at(-1) : expectedVariant;
    if (returnedVariant && returnedVariant !== expectedVariant && returnedVariant !== expectedVariantId) {
      droppedCount += 1;
      return null;
    }
    const props = {
      operation_id: handle.operationId,
      variant_id: operation.context.variant_id.startsWith('gid://') ? operation.context.variant_id : `gid://shopify/ProductVariant/${operation.context.variant_id}`,
      quantity: operation.context.quantity,
      added_quantity: operation.context.quantity,
      price_context: operation.context.price_context || { price_source: 'unknown', catalog_price: null, displayed_price: null, currency: null, pricing_mismatch: false },
      entry_point: operation.context.entry_point,
      ...(operation.context.product_id ? { product_id: operation.context.product_id } : {}),
    };
    const originalUnitPrice = Number(line.price);
    const price = Number(line.final_price ?? line.price);
    const currency = response.currency;
    if (Number.isFinite(originalUnitPrice) && originalUnitPrice >= 0 && Number.isFinite(price) && price >= 0 && /^[A-Z]{3}$/.test(currency || '')) {
      props.price_context = {
        price_source: 'ajax_cart', catalog_price: originalUnitPrice / 100, displayed_price: price / 100,
        currency, display_currency: currency, display_discount_percent: null, pricing_mismatch: false,
      };
      props.value = price * operation.context.quantity / 100;
      props.currency = currency;
    }
    return capture('product_added_to_cart', props);
  }

  function cartFailed(handle, error = {}) {
    if (!handle || !allowedForEpoch(handle.epoch)) return null;
    const operation = cartOperations.get(handle.operationId);
    if (!operation) return null;
    cartOperations.delete(handle.operationId);
    const status = Number.isInteger(error.http_status) ? error.http_status : undefined;
    const code = ['out_of_stock', 'quantity_limit', 'validation', 'http_error', 'network_error', 'response_parse_error', 'api_unavailable', 'unknown'].includes(error.error_code) ? error.error_code : status === 422 ? 'out_of_stock' : status ? 'http_error' : 'network_error';
    return capture('cart_action_failed', {
      operation_id: handle.operationId, action: 'add', failure_stage: error.failure_stage || 'mutation', error_code: code,
      ...(status ? { http_status: status } : {}), variant_id: operation.context.variant_id,
    });
  }

  const api = {
    capture,
    startCartOperation,
    cartSucceeded,
    cartFailed,
    startCartBatchOperation,
    cartBatchSucceeded,
    cartBatchFailed,
    cartUpdated,
    cartActionFailed,
    cartUpdatesFailed,
    createOperationId: createId,
    trackFilterChange,
    prepareSearchNavigation,
    onResultsRendered,
    onRecommendationsRendered,
    setReplayContext: () => getReplay(),
    trackCartView,
    getCurrentFilters: () => state === 'ALLOWED' ? collectFilterValues() : {},
    uiFailure,
    setTrafficType: (value) => {
      if (state !== 'ALLOWED' || !['internal', 'test'].includes(value)) return false;
      trafficType = value;
      safeWrite(TRAFFIC_KEY, value);
      return true;
    },
    revokeConsent: () => setConsent('DENIED'),
    getDiagnostics: () => config.environment === 'production' ? { state } : { state, queue: publishQueue.length, dropped: droppedCount, activeOperations: cartOperations.size },
  };
  win.TycheAnalytics = Object.freeze(api);
  setupConsent();
  setupListeners();
  return win.TycheAnalytics;
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') startStorefrontAnalytics();
